// janus-engine/ability/range-check.ts — lane RX (2026-09-29): is_target_in_range()['in_range'] for the frames the browser gate never
// judges — a janus_trigger dispatch at its inciting creature (validate_and_adjust_targets' SILENT range skip in a trigger frame) and
// a counterspell watch. Distances are the FRONT-END combat distance (ft3d-combat-distance-v3, owner 2026-09-28: the page's rule for
// any size — never stricter than PHP's centre measure); the reach band (a numeric range ≤ 15, or RangeType reach) is the caster's reach
// ring on the target's footprint (rulesWithinReach). Anything the twin does not model hands back.
import { type JanusCtx, type Row, handBack, itemField, record, upper } from './context.js';
import { rulesWithinReach } from './templates/template-weapon-attack-roll.js';
import { pageDistanceFt } from '../reactions/page-trigger-readers.js';
import { TriggerInputUnavailable } from '../reactions/trigger-dispatch.js';
import { effectiveSpellRangeId, persistentSpellRangeMultiplier, spellRangeMultiplier, throughSummonOrigins } from '../actions/cast-range-gate.js';
import type { AbilityPreconditionReaders } from '../actions/ability-preconditions.js';
import contract from '../compile/c1-contract.json';

const SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const REACH = '36D59FF4-6778-96ED-8664-F5D63638C77B';
/** RangeType x_distance (includes/additions.php RangeType::LIST). */
const RANGE_FT: Record<string, number | 'unlimited'> = {
  '4C761395-2532-3F23-7CBA-CA81AF5C0855': 30, 'A95F219A-6288-6B17-6DB7-03DE4B97AE96': 60, 'D1E8DA38-61C2-97AA-6173-4BF6157FEEE3': 120,
  'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA': 240, 'BB47D2E8-FFFD-8A04-FC6C-FC02583750ED': 'unlimited',
};

export function abilityRangeAllows(ctx: JanusCtx, caster: Row, target: Row, ability: Row, reason = 'is_target_in_range',
  /** false = an ordinary cast frame (counterspell_check during a cast): the Distant multiplier applies there (Codex D-050 review P2). */
  triggerFrame = true): boolean {
  let range = ctx.read.value(ability, 'range');
  if (range == null || range === '' || range === 0 || range === '0') return false; // PHP: empty(range) → not in range
  if (ctx.read.has(ability, 'caster_moves_towards_target_in_ft') || record(ability.attack_ability_arr)) handBack(`${reason}:move-towards`);
  // D-050 (owner 2026-09-30): the range extensions are the front end's. The Distant multiplier (ft_sanguine_next_spell_range_multiplier) is
  // 1 in a trigger dispatch; an ordinary frame (the counterspell watch) applies it — its reach band with a multiplier is not modelled (hand
  // back). Far Bender (effectiveSpellRangeId) raises the measured range; CAST-THROUGH-SUMMON swaps the measuring body to the closest
  // controlled summon (strictly closer than the caster), as is_target_in_range() does.
  let measuring = caster;
  let multiplier = 1;
  if (String(itemField(ability, 'item_type') ?? '') === 'spell') {
    const pre = { value: (row: Row | null, key: string) => row ? ctx.read.value(row, key) : null, has: (row: Row | null, key: string) => !!row && ctx.read.has(row, key),
      statuses: (row: Row) => ctx.read.statuses(row), inCombat: () => false, entries: (row: Row | null, key: string) => row ? ctx.read.entries(row, key) : [],
      resolveItem: (entry: unknown) => { const e = record(entry); const id = upper(typeof entry === 'string' ? entry : e?.ability_id ?? record(e?.items)?.item_id ?? e?.item_id);
        return id ? ctx.read.definition(id) : null; }, skillEntries: contract.skillEntries } as unknown as AbilityPreconditionReaders;
    const roster = Array.isArray(ctx.encounter.encounter_creatures_arr) ? (ctx.encounter.encounter_creatures_arr as unknown[]).map(record).filter((r): r is Row => r !== null) : [];
    // Far Bender is janus()'s (computed.range, raised before validation): a trigger dispatch runs through janus(); counterspell_check()
    // measures the RAW catalog item (discern_item_id) — no upgrade there
    const effective = triggerFrame ? effectiveSpellRangeId(caster, ability, pre) : upper(range);
    if (effective === undefined) handBack(`${reason}:far-bender-unproved`);
    range = effective;
    const persistent = persistentSpellRangeMultiplier(caster, ability, pre);
    if (persistent === undefined) handBack(`${reason}:persistent-range-unproved`);
    multiplier = Math.min(10, persistent * spellRangeMultiplier(caster, ability, pre, triggerFrame));
    const summons = throughSummonOrigins(caster, ability, roster, pre, effective);
    if (summons === undefined) handBack(`${reason}:range-through-summon-unproved`);
    if (summons?.length) {
      const host0 = { value: (row: Row, key: string) => ctx.read.value(row, key), entries: (row: Row, key: string) => ctx.read.entries(row, key) };
      const measure = (from: Row): number | null => { try { return pageDistanceFt(host0, from, target); } catch (error) { if (error instanceof TriggerInputUnavailable) handBack(`${reason}:${error.message}`); throw error; } };
      let best = measure(caster);
      if (best === null) handBack(`${reason}:distance-unknown`);
      for (const summon of summons) {
        const d = measure(summon);
        if (d === null) handBack(`${reason}:distance-unknown`);
        if (d! < best!) { best = d; measuring = summon; }
      }
    }
  }
  let allowed: number | 'unlimited' | 'reach';
  if (typeof range === 'number' || /^\d+$/.test(String(range))) allowed = Number(range);
  else if (upper(range) === REACH) allowed = 'reach';
  else if (upper(range) === SELF) handBack(`${reason}:self-range`);
  else {
    const known = RANGE_FT[upper(range)];
    if (known === undefined) handBack(`${reason}:range-type-not-modelled`);
    allowed = known;
  }
  if (allowed === 'unlimited') return true;
  if (allowed === 'reach' || allowed <= 15) {
    if (multiplier !== 1) handBack(`${reason}:reach-multiplier`);
    return rulesWithinReach(ctx, measuring, target);
  }
  allowed *= multiplier;
  const host = { value: (row: Row, key: string) => ctx.read.value(row, key), entries: (row: Row, key: string) => ctx.read.entries(row, key) };
  let distance: number | null;
  try { distance = pageDistanceFt(host, measuring, target); }
  catch (error) { if (error instanceof TriggerInputUnavailable) handBack(`${reason}:${error.message}`); throw error; }
  // PHP's calculate_range_from_caster_to_target() returns null for a sizeless caster (and null <= N is TRUE in PHP) — an unknown
  // measure is never a silent veto here (Codex review 2026-09-29): hand back.
  if (distance === null) handBack(`${reason}:distance-unknown`);
  return distance <= allowed;
}
